"use client";
import Typography from "@mui/material/Typography";
import { t, Trans } from "@lingui/macro";
import Box from "@mui/material/Box";
import React, { useRef, useEffect } from "react";
import { baseUrl } from "@/utils/api-definition";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "@mui/material/Button";
import { ChevronLeft } from "@mui/icons-material";
import { CardMedia } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import price from "@/utils/price";
import DeliveryOptions from "./delivery";
import PreviewModalContent from "./preview";
import Modal from "@mui/material/Modal";
import { useAlertStore } from "@/components/context/alert-store";
import { useLingui } from "@lingui/react";
import GiftImage from "@/components/core/gift/gift-image";
import AddToCartSuccessMessage from "@/components/core/alert-messages/add-cart-success";
import { AxiosError } from "axios";
import { useRouter } from "next/navigation";
import { sendGAEventTrigger } from "@/utils/analytics-trigger";
import { getRequest, postRequest } from "@/utils/request";
import { useGiftStore } from "@/components/context/gift-store";
import { useProductStore } from "@/components/context/product-store";
import { useShallow } from "zustand/react/shallow";

interface CustomizeProductProps {
  gift: any;
  lang: string;
  presetProduct: string | null;
}
const CustomizeProduct: React.FC<CustomizeProductProps> = ({ gift, lang, presetProduct }) => {
  const {
    setPriceDisplay,
    priceDisplay,
    productOption,
    hasPriceOption,
    errors,
    product,
    setProductId,
    setProductOption,
    setErrors,
    setErrorMessage,
    setCustomizeProduct,
    countryCode,
    deliveryDate,
    messageField,
    deliveryDateExact,
    deliveryMethod,
    name,
    email,
    phone,
    open,
    submitted,
    setDeliveryDate,
    setMessageField,
    setDeliveryDateExact,
    setFinish,
    setDeliveryMethod,
    setName,
    setEmail,
    setPhone,
    setOpen,
    setSubmitted,
    productCard,
    previewUploadedFile,
    uploadedFile,
    blob,
    setProductCard,
    setPreviewUploadedFile,
    setUploadedFile,
    setSelectedCategory,
    selectedBackground,
    setSelectedBackground,
  } = useGiftStore();
  const resetQty = useProductStore((state) => state.resetQty);

  const { i18n } = useLingui();

  const { setMessage } = useAlertStore();
  const imagePath = useRef<string>("");

  const queryClient = useQueryClient();
  const handleClose = () => setOpen(false);
  const { data, isPending } = useQuery({
    queryKey: ["customizeOptions", product],
    queryFn: () =>
      getRequest({
        path: { name: "giftSettings", id: `${product?.id}` },
        lang,
      }),
    enabled: !!product?.id,
  });
  const { qty: quantity } = useProductStore(
    useShallow((state) => ({
      qty: state.qty,
      setQty: state.setQty,
    })),
  );
  const createFormData = () => {
    if (product) {
      const formData = new FormData();

      formData.append("product_id", product?.id);
      formData.append("quantity", quantity.toString());
      formData.append("checkout_as_gift", "1");
      formData.append("recipient_name", name);
      formData.append("message", messageField);
      formData.append("delivery_type", deliveryMethod);
      formData.append("date_option", deliveryDate);
      if (deliveryDate === "future") {
        formData.append("date", deliveryDateExact);
      }

      productOption.forEach((pair) => {
        if (pair.value) formData.append(`option[${pair.key}]`, pair.value);
      });

      formData.append("design_id", selectedBackground.model_id ? selectedBackground.model_id : "");

      if (phone) {
        formData.append("sms", phone);
        formData.append("sms_prefix_code", countryCode);
      }
      if (email) {
        formData.append("email", email);
      }
      if (blob) {
        formData.append("attachment", blob);
        formData.append("attachment_name", "uploaded");
      } else if (productCard.name) {
        formData.append("attachment_name", productCard.name);
      } else {
        const image = product.original_image.replace(baseUrl + "/image/", "");
        formData.append("attachment_name", image);
      }
      if (gift) {
        formData.append("cart_id", gift.key);
      }
      return formData;
    } else return false;
  };
  const validateInputs = () => {
    let valid = true;
    if (!deliveryMethod) {
      valid = false;
    }
    if (!deliveryDate) {
      valid = false;
    }
    if (!name) {
      valid = false;
    }
    if (!messageField) {
      valid = false;
    }
    if (deliveryDate === "future") {
      const currentDate = new Date(Date.now());
      if (!deliveryDateExact || new Date(currentDate) > new Date(deliveryDateExact)) {
        setMessage({
          text: t(i18n)`Delivery date should be set in the future!`,
          severity: "error",
        });
        valid = false;
      }
    }
    if (deliveryMethod === "email") {
      if (!email) {
        setErrorMessage("email", t(i18n)`Email required!`);
        valid = false;
      }
      const isValidEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g;
      if (!email.match(isValidEmail)) {
        setErrorMessage("email", t(i18n)`Email is not valid!`);
        valid = false;
      } else {
        const oldErrors = errors.map((item: any) =>
          item.key === "email" ? { key: item.key, error: null } : item,
        );
        setErrors(oldErrors);
      }
    }
    if (deliveryMethod === "sms") {
      if (!phone) {
        setErrorMessage("phone", t(i18n)`Phone required!`);
        valid = false;
      }
      if (!phone.match("[0-9]{9}")) {
        setErrorMessage("phone", t(i18n)`Phone is not valid!`);
        valid = false;
      } else {
        const oldErrors = errors.map((item: any) =>
          item.key === "phone" ? { key: item.key, error: null } : item,
        );
        setErrors(oldErrors);
      }
    }
    return valid;
  };
  const handleUpdate = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    setFinish(true);
    if (validateInputs()) {
      const formData = createFormData();
      if (formData) {
        updateMutation.mutate(formData);
      }
    }
  };

  const checkIfSubmitAndErroredFields = () => {
    if (!validateInputs()) {
      setSubmitted(false);
    }
  };

  const handleAddToCart = (event: any) => {
    setSubmitted(true);
    event.preventDefault();
    event.stopPropagation();
    setFinish(true);
    if (validateInputs()) {
      const formData = createFormData();
      if (formData) {
        addToCartMutation.mutate(formData);
        sendGAEventTrigger("Add To Cart", {
          send_to: `${process.env.NEXT_PUBLIC_GTM_MEASUREMENT_ID}`,
          category: "ecommerce",
          label: product?.name,
        });
      }
    }
  };

  const updateMutation = useMutation({
    mutationFn: (formData: any) => postRequest({ path: { name: "giftEdit" }, formData, lang }),
    onSuccess: () => {
      setMessage({
        text: (
          <AddToCartSuccessMessage
            item={{
              seo_url: product ? product.seo_url : "",
              name: product ? product.name : "",
              edited: true,
            }}
          />
        ),
        severity: "success",
      });
      router.push("/checkout");
    },
    onError: () => {},
  });

  const router = useRouter();
  const addToCartMutation = useMutation({
    mutationFn: (formData: any) => postRequest({ path: { name: "giftAdd" }, formData, lang }),
    onSuccess: (data) => {
      if (data.success === 1) {
        queryClient.invalidateQueries({ queryKey: ["cartQuantity"] });
        setMessage({
          text: (
            <AddToCartSuccessMessage
              item={{
                seo_url: product ? product.seo_url : "",
                name: product ? product.name : "",
              }}
            />
          ),
          severity: "success",
        });
      } else {
        if (data.error && data.error[0]) {
          if (data.error[0].maximum) {
            // If the error object has a "maximum" property:
            setMessage({
              text: data.error[0].maximum,
              severity: "error",
            });
          } else if (data.error[0].option) {
            // If the error object has an "option" property, extract the first key and its message.
            const optionKeys = Object.keys(data.error[0].option);
            if (optionKeys.length > 0) {
              setMessage({ text: data.error[0].option[optionKeys[0]], severity: "error" });
            } else {
              // Fallback if the option object is empty.
              setMessage({ text: "An unknown option error occurred.", severity: "error" });
            }
          } else {
            // Fallback error message if structure is unexpected.
            setMessage({ text: "An unknown error occurred.", severity: "error" });
          }
        }
      }

      setProductId("");
      setCustomizeProduct(false);
      resetQty();
      if (productOption || quantity || productOption) {
        router.push("/create-gift");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.description);
      }
    },
  });

  useEffect(() => {
    if (gift) {
      setName(gift.options.recipient);
      setDeliveryMethod(gift.options.deliveryType);
      setDeliveryDate(gift.options.deliveryDateOption);
      if (gift.options.deliveryDateOption === "future") {
        setDeliveryDateExact(gift.options.deliveryDate);
      }
      if (gift.options.deliveryType === "email") {
        setEmail(gift.options.deliveryData);
      } else if (gift.options.deliveryType === "sms") {
        setPhone(gift.options.deliveryData.split(gift.options.deliverySmsDialCode)[1]);
      }
      setMessageField(gift.options.message);
      setProductCard({
        src: `${baseUrl}/image/` + gift.options.attachment,
        name: gift.options.attachment,
        id: "",
      });
      if (gift.options.attachment.includes("uploaded/")) {
        const uploadedFileObject = {
          name: gift.options.attachment.split("uploaded/")[1],
          type: "image/",
        };
        if (gift.options.attachment.includes(".mp4")) {
          uploadedFileObject.type = "video/";
        }
        setPreviewUploadedFile(productCard.src);
        setUploadedFile(uploadedFileObject);
      }
      setGiftCategory(gift);

      if (hasPriceOption.option_id) {
        const giftPrice = gift.option.find((option: any) => option.type === "text");

        if (giftPrice) {
          const updatedArray = [...productOption];
          updatedArray.push({ key: hasPriceOption.product_option_id, value: giftPrice.value });
          setProductOption(updatedArray);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gift, hasPriceOption, data]);

  useEffect(() => {
    const validQuantity = Number(quantity) || 0;

    const priceOption = hasPriceOption.product_option_id
      ? productOption.find((option) => hasPriceOption.product_option_id === option.key)
      : undefined;

    const price =
      hasPriceOption.product_option_id && priceOption
        ? validQuantity * Number(priceOption.value)
        : product.special === 0
          ? validQuantity * Number(product.price)
          : validQuantity * Number(product.special);

    setPriceDisplay(price);
  }, [quantity, productOption, hasPriceOption]);

  const setGiftCategory = (gift: { options: { designId: string } }) => {
    const categories = data?.data?.categories;
    for (const categoryIndex in categories) {
      if (Array.isArray(categories[categoryIndex].models)) {
        const entry = categories[categoryIndex].models.find(
          (item: { model_id: string }) => item.model_id === String(gift.options.designId),
        );
        if (entry) {
          setSelectedBackground(entry);
          setSelectedCategory({
            category_id: categories[categoryIndex].category_id,
            models: categories[categoryIndex].models,
          });
        }
      }
    }
    return false;
  };

  return (
    <>
      <div className="mb-2.5 flex w-full flex-col items-start text-sm md:w-[60%]">
        <Box
          sx={{
            position: { xs: "relative", md: "sticky" },
            top: { xs: 0, md: 105 },
            zIndex: 1,
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {!gift && !presetProduct && (
            <Button
              sx={{ p: 0, width: "fit-content" }}
              onClick={() => {
                setCustomizeProduct(false);
                setErrors([]);
              }}
            >
              <ChevronLeft className="!w-[40%] text-[34px]" />
              <Trans>Previous</Trans>
            </Button>
          )}

          <Box
            sx={{
              width: "100%",
              height: "100%",
              pt: gift ? { xs: "10px", md: "35px" } : 0,
            }}
          >
            {selectedBackground && (
              <Box
                sx={{
                  padding: "20px",
                  margin: "15px auto 30px auto",
                  height: { xl: "458px", md: "326px", sm: "calc(100vw - 30px)", xs: "100vw" },
                  width: { xl: "458px", md: "326px", sm: "calc(100vw - 30px)", xs: "100%" },
                  background: "#f6f6f6",
                  display: "flex",
                  alignItems: "center",
                  backgroundImage: selectedBackground.image
                    ? `url(${imagePath.current + selectedBackground.image})`
                    : `linear-gradient(${selectedBackground.angle}, ${selectedBackground.hex_start} 0%, ${selectedBackground.hex_end} 100%)`,
                  backgroundSize: "cover",
                }}
              >
                {previewUploadedFile ? (
                  uploadedFile?.type?.includes("image/") ? (
                    <Box
                      sx={{
                        backgroundImage: `url(${previewUploadedFile})`,
                        height: { md: "250px", xs: "180px" },
                        width: { md: "362px", xs: "260px" },
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        margin: "0 auto",
                        borderRadius: "10px",
                      }}
                    ></Box>
                  ) : (
                    <CardMedia
                      sx={{
                        height: "250px",
                        // width: "362px",
                        backgroundSize: "contain",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        margin: "0 auto",
                        borderRadius: "10px",
                        maxHeight: "167px",
                        maxWidth: "280px",
                        width: "100%",
                        padding: "20px",
                      }}
                      component="video"
                      autoPlay
                      image={previewUploadedFile}
                    />
                  )
                ) : (
                  <Box
                    sx={{
                      backgroundImage: `url(${productCard.src})`,
                      height: { md: "180px", sm: "250px", xs: "180px" },
                      width: { md: "260px", sm: "362px", xs: "260px" },
                      backgroundSize: "contain",
                      backgroundRepeat: "no-repeat",
                      backgroundPosition: "center",
                      margin: "0 auto",
                      borderRadius: "10px",
                    }}
                  ></Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </div>
      <div className="-mt-8 mb-2.5 flex w-full text-sm md:mt-8 md:!w-[50%]">
        <Box className="mx-auto w-full max-w-[450px] md:max-w-none">
          <Box>
            <Typography
              sx={{
                fontSize: { xs: "24px", md: "32px" },
                fontWeight: 600,
                marginTop: { xs: "5px", md: "20px" },
                marginBottom: { xs: "15px", md: "40px" },
                lineHeight: "1.2",
              }}
            >
              <Trans>{product?.name}</Trans>
            </Typography>
          </Box>
          <GiftImage data={data} imagePath={imagePath} gift={gift} isPending={isPending} />

          <DeliveryOptions checkIfSubmitAndErroredFields={checkIfSubmitAndErroredFields} />
          <Box sx={{ margin: "40px 0", borderTop: "1px solid #d2d2d7", width: "100%" }}> </Box>
          <Box
            sx={{
              padding: "25px",
              background: "#f2f2f3",
              borderRadius: "8px",
              marginTop: "40px",
              marginBottom: "50px",
              width: "100%",
              display: "block",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                columnGap: "20px",
                width: "100%",
                marginBottom: "30px",
              }}
            >
              <Typography sx={{ fontSize: "20px", maxWidth: "180px", fontWeight: "bold" }}>
                <Trans>Total</Trans>:
              </Typography>
              {product?.id && (
                <Typography
                  sx={{
                    fontWeight: "500",
                    fontSize: "16px",
                    lineHeight: 1.31,
                  }}
                >
                  {price(priceDisplay)}
                </Typography>
              )}
            </Box>
            <Typography
              sx={{
                fontWeight: "400",
                fontSize: "14px",
                margin: "30px 0px",
              }}
            >
              <InfoIcon sx={{ margin: "0px 5px 3px 0px", fontSize: "22px" }} />
              {(deliveryMethod == "email" || deliveryMethod == "sms") && (
                <>
                  {deliveryMethod == "sms" && (
                    <>
                      <Trans>We send your gift sms to</Trans>&nbsp;
                      {phone ? "+" + countryCode + phone : "..."}
                    </>
                  )}
                  {deliveryMethod == "email" && (
                    <>
                      <Trans>We send your gift email to </Trans>&nbsp;{email || "..."}
                    </>
                  )}
                  &nbsp;
                  {deliveryDateExact && deliveryDate === "future"
                    ? t(i18n)`at` +
                      " " +
                      new Date(deliveryDateExact).toISOString().substring(0, 16).replace("T", " ")
                    : t(i18n)` after the payment`}
                </>
              )}
              {deliveryMethod == "link" && (
                <Trans>You will get a Link which you can share after the payment.</Trans>
              )}
              {deliveryMethod == "print" && (
                <Trans>You will be able to download and print your Gift after the payment.</Trans>
              )}

              {!deliveryMethod && <Trans>We send your gift ... to ...</Trans>}
            </Typography>
            {gift ? (
              <Button
                data-testid="customize-gift"
                variant="contained"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  padding: "10px 5px",
                  borderRadius: "4px",
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.15)",
                  width: "100%",
                }}
                onClick={(event) => handleUpdate(event)}
              >
                <Trans>Update</Trans>
              </Button>
            ) : (
              <Button
                data-testid="customize-gift"
                variant="contained"
                sx={{
                  fontSize: "16px",
                  fontWeight: 400,
                  padding: "10px 5px",
                  borderRadius: "4px",
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.15)",
                  width: "100%",
                }}
                disabled={submitted}
                onClick={(event) => handleAddToCart(event)}
              >
                <Trans>Add to shopping cart</Trans>
              </Button>
            )}

            <Button
              data-testid="customize-gift"
              variant="contained"
              sx={{
                fontSize: "16px",
                fontWeight: 400,
                border: "solid 2px #0097ab",
                borderRadius: "4px",
                width: "100%",
                backgroundColor: "#fff",
                padding: "10px 5px",
                marginTop: "10px",
                color: "#0097ab",
              }}
              onClick={() => {
                setFinish(true);
                setOpen(true);
              }}
            >
              <Trans>Preview</Trans>
            </Button>
          </Box>
          {product && (
            <Modal
              sx={{ overflow: "auto" }}
              open={open}
              onClose={handleClose}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
            >
              <PreviewModalContent
                messageField={messageField}
                name={name}
                previewUploadedFile={previewUploadedFile}
                productCard={productCard}
                selectedBackground={selectedBackground}
                imagePath={imagePath}
                uploadedFile={uploadedFile}
                product={product}
                handleClose={handleClose}
              />
            </Modal>
          )}
        </Box>
      </div>
    </>
  );
};
export default CustomizeProduct;
